/** Batch "Reimport from stored XML" dialog for the Bookings grid (a custom
 *  `dialog(ctx)` batch action).
 *
 *  Bookings from a restech can be re-pulled with the Data Sync tool. Tenants
 *  that push cp-import XML at us directly have no equivalent, so this replays
 *  each selected booking's most recent stored import document.
 *
 *  The backend is authoritative on every safety question here (which stored run
 *  is used, whether the document can be replayed at all, whether removing a
 *  booking is recoverable); this dialog only collects intent. Run a dry run
 *  first: it resolves and slices exactly as the real run would and reports a
 *  per-booking outcome in Background Tasks, writing nothing.
 */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { HelpLink } from '@/components/HelpLink';

type DocumentScope = 'selected' | 'whole';
type Removal = 'none' | 'cancel' | 'delete';
type Tristate = 'default' | 'send' | 'skip';

const TRISTATE_OPTIONS: Array<{ value: Tristate; label: string }> = [
  { value: 'skip', label: 'Skip — never send' },
  { value: 'default', label: 'Default — let the stored XML decide' },
  { value: 'send', label: 'Send — force on' },
];

export function ReimportXmlDialog({ ctx }: { ctx: BatchActionContext }) {
  const [documentScope, setDocumentScope] = useState<DocumentScope>('selected');
  const [removal, setRemoval] = useState<Removal>('none');
  const [sendNotifications, setSendNotifications] = useState<Tristate>('skip');
  const [sendSurveys, setSendSurveys] = useState<Tristate>('skip');
  const [allowPartialSource, setAllowPartialSource] = useState(false);
  const [reason, setReason] = useState('');

  const destructive = removal !== 'none';
  const canSubmit = !destructive || reason.trim().length > 0;

  const submit = (dryRun: boolean) => {
    if (!canSubmit) return;
    void ctx.runStandard({
      dry_run: dryRun,
      document_scope: documentScope,
      removal,
      send_notifications: sendNotifications,
      send_surveys: sendSurveys,
      ...(destructive && allowPartialSource ? { allow_partial_source: true } : {}),
      ...(destructive ? { reason: reason.trim() } : {}),
    });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>
        Reimport from stored XML <HelpLink story="reimport-bookings" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography variant="body2" color="text.secondary">
            Replays the most recent stored import document for{' '}
            {ctx.count.toLocaleString()} booking(s). Runs in the background — capped at
            1,000 bookings per batch.
          </Typography>

          <FormControl>
            <FormLabel>Document</FormLabel>
            <RadioGroup
              value={documentScope}
              onChange={(e) => setDocumentScope(e.target.value as DocumentScope)}
            >
              <FormControlLabel
                value="selected"
                control={<Radio size="small" />}
                label="Only the selected bookings (recommended)"
              />
              <FormControlLabel
                value="whole"
                control={<Radio size="small" />}
                label="The whole stored document"
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              {documentScope === 'selected'
                ? 'The stored document is trimmed to the selected bookings. Bookings sharing an order or a package travel with them, so shared data stays complete.'
                : 'Every booking in each stored document is reimported, not just the selected ones. One sync document can hold hundreds.'}
            </Typography>
          </FormControl>

          <FormControl>
            <FormLabel>Before reimporting</FormLabel>
            <RadioGroup
              value={removal}
              onChange={(e) => setRemoval(e.target.value as Removal)}
            >
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label="Leave the booking in place (recommended)"
              />
              <FormControlLabel
                value="cancel"
                control={<Radio size="small" />}
                label="Cancel it first, silently"
              />
              <FormControlLabel
                value="delete"
                control={<Radio size="small" />}
                label="Delete it first, silently"
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              {removal === 'none'
                ? 'The reimport updates each booking in place.'
                : 'No cancellation notification is sent. Sub-bookings go with the booking.'}
            </Typography>
          </FormControl>

          {destructive && (
            <Alert severity="warning">
              <AlertTitle>
                {removal === 'delete' ? 'Bookings will be deleted' : 'Bookings will be cancelled'}
              </AlertTitle>
              Bookings whose stored document cannot recreate them are skipped rather than
              removed. Run a dry run first and read the outcomes in Background Tasks.
            </Alert>
          )}

          <TextField
            select
            size="small"
            label="Guest notifications"
            value={sendNotifications}
            onChange={(e) => setSendNotifications(e.target.value as Tristate)}
            helperText="Overrides the per-booking flag in the stored document."
          >
            {TRISTATE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Trip surveys"
            value={sendSurveys}
            onChange={(e) => setSendSurveys(e.target.value as Tristate)}
            helperText="Replaying past-dated bookings can otherwise trigger surveys."
          >
            {TRISTATE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          {destructive && (
            <>
              <TextField
                size="small"
                fullWidth
                required
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                error={reason.trim().length === 0}
                helperText="Required — recorded on the audit trail."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allowPartialSource}
                    onChange={(e) => setAllowPartialSource(e.target.checked)}
                  />
                }
                label="Also remove bookings whose stored document is a partial update"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                Off by default. Such a document cannot recreate the booking it removes.
              </Typography>
            </>
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">Acting on {ctx.matchedPhrase} bookings.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="outlined" disabled={!canSubmit} onClick={() => submit(true)}>
          Dry run (preview)
        </Button>
        <Button
          variant="contained"
          color={destructive ? 'warning' : 'primary'}
          disabled={!canSubmit}
          onClick={() => submit(false)}
        >
          Reimport {ctx.count.toLocaleString()} booking(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
